/**
 * The error taxonomy (docs/16 section 9).
 *
 * Every failure resolves to exactly one of these, and each has one defined
 * presentation. The old frontend routed everything through a single toast, so an
 * out-of-credits user and a dropped connection looked identical.
 *
 * Only the classes Increment 1 can actually produce are handled here. `payment`
 * and `conflict` are declared because the client must classify a 402 or 409 if
 * one arrives rather than silently mislabel it, but no screen renders them yet.
 */

export type ApiErrorKind =
  | 'offline'
  | 'unauthenticated'
  | 'payment'
  | 'forbidden'
  | 'conflict'
  | 'validation'
  | 'server'
  | 'unexpected'

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number | null
  readonly detail: unknown

  constructor(kind: ApiErrorKind, message: string, status: number | null = null, detail: unknown = null) {
    super(message)
    this.name = 'ApiError'
    this.kind = kind
    this.status = status
    this.detail = detail
  }
}

/** Map a response status to its class. One place, so no call site guesses. */
export function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthenticated'
  if (status === 402) return 'payment'
  if (status === 403) return 'forbidden'
  if (status === 409) return 'conflict'
  if (status === 422 || status === 400) return 'validation'
  if (status >= 500) return 'server'
  return 'unexpected'
}

/**
 * The backend returns a structured `detail`, sometimes an object with a message.
 * Surface what it said rather than substituting a generic string.
 */
export function messageFromPayload(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) return payload
  if (payload && typeof payload === 'object') {
    const detail = (payload as { detail?: unknown }).detail
    if (typeof detail === 'string' && detail.trim()) return detail
    if (detail && typeof detail === 'object') {
      const message = (detail as { message?: unknown }).message
      if (typeof message === 'string' && message.trim()) return message
    }
    const message = (payload as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return fallback
}

/** What the user is told. Never the raw exception. */
export function userFacingMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.kind) {
      case 'offline':
        return 'No connection to YourDealPulse. Check your network — this will retry automatically.'
      case 'unauthenticated':
        return 'Your session has expired. Please sign in again.'
      case 'forbidden':
        return error.message || 'Your account does not have access to this.'
      case 'server':
        return 'YourDealPulse had a problem loading this. Trying again usually works.'
      default:
        return error.message || 'Something went wrong.'
    }
  }
  return 'Something went wrong.'
}
